import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  Plus, LogOut, Menu, X, Tag, Pencil, Trash2,
  TrendingUp, TrendingDown, Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoriesPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('expense');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', type: 'expense', parent_id: '', color: '#7C3AED'
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCategories(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const grouped = useMemo(() => {
    const byType = { income: [], expense: [] };
    const parents = {};
    categories.forEach(c => {
      if (!c.parent_id) {
        parents[c.id] = { ...c, children: [] };
        byType[c.type].push(parents[c.id]);
      }
    });
    categories.forEach(c => {
      if (c.parent_id && parents[c.parent_id]) {
        parents[c.parent_id].children.push(c);
      }
    });
    // Sort: system first, then alphabetical
    const sortFn = (a, b) => {
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
      return a.name.localeCompare(b.name);
    };
    byType.income.sort(sortFn);
    byType.expense.sort(sortFn);
    byType.income.forEach(p => p.children.sort(sortFn));
    byType.expense.forEach(p => p.children.sort(sortFn));
    return byType;
  }, [categories]);

  const parentOptions = useMemo(() => {
    // Only allow choosing as parent: top-level categories of the same type
    return categories.filter(c => !c.parent_id && c.type === form.type);
  }, [categories, form.type]);

  const openCreate = (type, parentId = '') => {
    setEditing(null);
    setForm({
      name: '',
      type,
      parent_id: parentId,
      color: type === 'expense' ? '#7C3AED' : '#10B981'
    });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      type: cat.type,
      parent_id: cat.parent_id || '',
      color: cat.color || '#7C3AED'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      if (editing) {
        await fetch(`${API_URL}/api/categories/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: form.name, color: form.color })
        });
      } else {
        await fetch(`${API_URL}/api/categories`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name.trim(),
            type: form.type,
            parent_id: form.parent_id || null,
            color: form.color
          })
        });
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (err) {
      console.error('Error saving category');
    }
  };

  const handleDelete = async (cat) => {
    if (cat.is_system) return;
    const childCount = categories.filter(c => c.parent_id === cat.id).length;
    const msg = childCount > 0
      ? `"${cat.name}" tiene ${childCount} subcategoría(s). ¿Eliminar todo?`
      : `¿Eliminar la categoría "${cat.name}"?`;
    if (!window.confirm(msg)) return;
    const token = localStorage.getItem('token');
    // Delete children first
    if (childCount > 0) {
      await Promise.all(
        categories
          .filter(c => c.parent_id === cat.id && !c.is_system)
          .map(c => fetch(`${API_URL}/api/categories/${c.id}`, {
            method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
          }))
      );
    }
    await fetch(`${API_URL}/api/categories/${cat.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    fetchCategories();
  };

  const toggleActive = async (cat) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/categories/${cat.id}`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !cat.is_active })
    });
    fetchCategories();
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const renderRow = (cat, isChild = false) => (
    <div
      key={cat.id}
      className={`flex items-center gap-3 py-3 ${isChild ? 'pl-12' : 'pl-4'} pr-3 border-b border-zinc-800 last:border-0 ${!cat.is_active ? 'opacity-50' : ''}`}
      data-testid={`cat-row-${cat.id}`}
    >
      <div
        className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${cat.color}33` }}
      >
        {cat.type === 'income'
          ? <TrendingUp className="h-4 w-4" style={{ color: cat.color }} />
          : <TrendingDown className="h-4 w-4" style={{ color: cat.color }} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-white truncate">{cat.name}</p>
          {cat.is_system && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 flex items-center gap-1">
              <Lock className="h-2.5 w-2.5" /> Sistema
            </span>
          )}
          {!cat.is_active && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">
              Inactiva
            </span>
          )}
        </div>
        {isChild && <p className="text-xs text-zinc-500">Subcategoría</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!isChild && (
          <Button
            variant="ghost" size="sm"
            className="text-zinc-400 hover:text-violet-300 h-8"
            onClick={() => openCreate(cat.type, cat.id)}
            data-testid={`add-sub-${cat.id}`}
            aria-label={`Añadir subcategoría a ${cat.name}`}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            <span className="text-xs hidden sm:inline">Sub</span>
          </Button>
        )}
        <Button
          variant="ghost" size="icon"
          className="h-8 w-8 text-zinc-400 hover:text-violet-400"
          onClick={() => openEdit(cat)}
          aria-label={`Editar ${cat.name}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost" size="icon"
          className="h-8 w-8 text-zinc-400 hover:text-amber-400"
          onClick={() => toggleActive(cat)}
          aria-label={cat.is_active ? `Desactivar ${cat.name}` : `Activar ${cat.name}`}
          title={cat.is_active ? 'Desactivar' : 'Activar'}
        >
          {cat.is_active ? <span className="text-xs">●</span> : <span className="text-xs text-zinc-600">○</span>}
        </Button>
        {!cat.is_system && (
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 text-zinc-400 hover:text-red-400"
            onClick={() => handleDelete(cat)}
            aria-label={`Eliminar ${cat.name}`}
            data-testid={`delete-cat-${cat.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderList = (list) => {
    if (loading) return <p className="text-zinc-500 p-6">Cargando…</p>;
    if (list.length === 0) {
      return (
        <div className="text-center py-12">
          <Tag className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 mb-4">No hay categorías de este tipo todavía</p>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => openCreate(activeTab)}>
            <Plus className="h-4 w-4 mr-2" /> Crear la primera
          </Button>
        </div>
      );
    }
    return (
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-0">
          {list.map(parent => (
            <div key={parent.id}>
              {renderRow(parent, false)}
              {parent.children.map(child => renderRow(child, true))}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="categories" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="categories" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Categorías</h2>
              <p className="text-zinc-400 text-sm">Organiza tus ingresos y gastos en categorías y subcategorías</p>
            </div>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => openCreate(activeTab)} data-testid="new-category-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva categoría
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="expense" data-testid="tab-expense">
              <TrendingDown className="h-3.5 w-3.5 mr-1.5" /> Gastos ({grouped.expense.length + grouped.expense.reduce((s, p) => s + p.children.length, 0)})
            </TabsTrigger>
            <TabsTrigger value="income" data-testid="tab-income">
              <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Ingresos ({grouped.income.length + grouped.income.reduce((s, p) => s + p.children.length, 0)})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="expense" className="mt-4">
            {renderList(grouped.expense)}
          </TabsContent>
          <TabsContent value="income" className="mt-4">
            {renderList(grouped.income)}
          </TabsContent>
        </Tabs>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editing
                  ? 'Editar categoría'
                  : (form.parent_id ? 'Nueva subcategoría' : 'Nueva categoría')}
              </DialogTitle>
              <DialogDescription>
                {form.parent_id && !editing
                  ? `Dentro de ${parentOptions.find(p => p.id === form.parent_id)?.name || 'categoría padre'}`
                  : (editing ? 'Solo puedes editar el nombre y color' : 'Define el nombre y tipo de la categoría')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="cat-name">Nombre</Label>
                <Input id="cat-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Ej: Supermercado, Bonus, Streaming…"
                  autoFocus
                  data-testid="cat-name-input"
                />
              </div>
              {!editing && (
                <div>
                  <Label htmlFor="cat-type">Tipo</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => setForm({ ...form, type: v, parent_id: '' })}
                    disabled={!!form.parent_id}
                  >
                    <SelectTrigger id="cat-type" className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Gasto</SelectItem>
                      <SelectItem value="income">Ingreso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!editing && (
                <div>
                  <Label htmlFor="cat-parent">Categoría padre (opcional)</Label>
                  <Select
                    value={form.parent_id || 'none'}
                    onValueChange={(v) => setForm({ ...form, parent_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger id="cat-parent" className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Sin padre (categoría principal)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin padre · Categoría principal</SelectItem>
                      {parentOptions.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="cat-color">Color</Label>
                <Input id="cat-color" type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 h-10 w-full" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-category-btn">
                  {editing ? 'Guardar cambios' : 'Crear categoría'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CategoriesPage;
